import { defineStore } from 'pinia';
import { ref } from 'vue';
import { getAttachments, uploadAttachment, downloadAttachment, deleteAttachment, getAttachmentCounts, type AttachmentResponse, type CreateAttachmentRequest } from '@/api/endpoints/attachments';
import { useNotificationsStore } from '@/store/notificationsStore';
import { downloadBlob } from '@/utils/download';

export const useAttachmentStore = defineStore('attachment', () => {
  const attachments = ref<AttachmentResponse[]>([]);
  const attachmentCounts = ref<Record<number, number>>({});
  const loading = ref(false);
  const uploading = ref(false);
  // Forhåndsvisnings-tilstand for AttachmentDialog: komponenten leser disse reaktivt,
  // feilfangsten for nedlasting av preview-innhold ligger i loadPreview-actionen.
  const previewLoading = ref(false);
  const previewError = ref<string | null>(null);
  const previewUrl = ref<string | null>(null);
  const previewText = ref('');
  const notificationsStore = useNotificationsStore();

  async function fetchAttachments (rowId: number) {
    try {
      loading.value = true;
      attachments.value = await getAttachments(rowId);
      attachmentCounts.value[rowId] = attachments.value.length;
    } catch (error: any) {
      const problem = error?.response?.data;
      if (problem?.title) {
        notificationsStore.addProblem(problem);
      } else {
        notificationsStore.addError('Kunne ikke laste vedlegg', problem?.detail || 'En uventet feil oppstod');
      }
      attachments.value = [];
    } finally {
      loading.value = false;
    }
  }

  async function uploadFile (request: CreateAttachmentRequest) {
    try {
      uploading.value = true;
      const newAttachment = await uploadAttachment(request);
      attachments.value.push(newAttachment);
      // Oppdater count for raden
      const currentCount = attachmentCounts.value[request.rowId] || 0;
      attachmentCounts.value[request.rowId] = currentCount + 1;
      notificationsStore.addSuccess('Vedlegg lastet opp');
      return newAttachment;
    } catch (error: any) {
      const problem = error?.response?.data;
      if (problem?.title) {
        notificationsStore.addProblem(problem);
      } else {
        notificationsStore.addError('Kunne ikke laste opp vedlegg', problem?.detail || 'En uventet feil oppstod');
      }
      throw error;
    } finally {
      uploading.value = false;
    }
  }

  async function downloadFile (attachmentId: number, fileName: string, autoDownload: boolean = true) {
    try {
      const blob = await downloadAttachment(attachmentId);
      if (autoDownload) {
        downloadBlob(blob, fileName);
      }
      return blob;
    } catch (error: any) {
      const problem = error?.response?.data;
      if (problem?.title) {
        notificationsStore.addProblem(problem);
      } else {
        notificationsStore.addError('Kunne ikke laste ned vedlegg', problem?.detail || 'En uventet feil oppstod');
      }
      throw error;
    }
  }

  async function removeAttachment (attachmentId: number) {
    try {
      const attachment = attachments.value.find(a => a.id === attachmentId);
      await deleteAttachment(attachmentId);
      attachments.value = attachments.value.filter(a => a.id !== attachmentId);
      // Oppdater count for raden
      if (attachment) {
        const currentCount = attachmentCounts.value[attachment.icsRowId] || 0;
        attachmentCounts.value[attachment.icsRowId] = Math.max(0, currentCount - 1);
      }
      notificationsStore.addSuccess('Vedlegg slettet');
    } catch (error: any) {
      const problem = error?.response?.data;
      if (problem?.title) {
        notificationsStore.addProblem(problem);
      } else {
        notificationsStore.addError('Kunne ikke slette vedlegg', problem?.detail || 'En uventet feil oppstod');
      }
      throw error;
    }
  }

  async function loadPreview (attachment: AttachmentResponse, mode: 'objectUrl' | 'text') {
    clearPreview();
    previewLoading.value = true;
    try {
      const blob = await downloadFile(attachment.id, attachment.originalFileName, false);
      if (mode === 'objectUrl') {
        previewUrl.value = window.URL.createObjectURL(blob);
      } else {
        previewText.value = await blob.text();
      }
    } catch (error: any) {
      previewError.value = 'Kunne ikke laste forhåndsvisning: ' + (error?.message || 'Ukjent feil');
    } finally {
      previewLoading.value = false;
    }
  }

  function clearPreview () {
    if (previewUrl.value) {
      window.URL.revokeObjectURL(previewUrl.value);
    }
    previewUrl.value = null;
    previewText.value = '';
    previewError.value = null;
  }

  async function fetchAttachmentCounts (rowIds: number[]) {
    try {
      const counts = await getAttachmentCounts(rowIds);
      // Merge new counts with existing ones
      attachmentCounts.value = { ...attachmentCounts.value, ...counts };
    } catch (error: any) {
      const problem = error?.response?.data;
      if (problem?.title) {
        notificationsStore.addProblem(problem);
      } else {
        notificationsStore.addError('Kunne ikke laste vedleggsantall', problem?.detail || 'En uventet feil oppstod');
      }
    }
  }

  function getAttachmentCount (rowId: number): number {
    return attachmentCounts.value[rowId] ?? 0;
  }

  function reset () {
    attachments.value = [];
    loading.value = false;
    uploading.value = false;
  }

  function resetCounts () {
    attachmentCounts.value = {};
  }

  return {
    attachments,
    attachmentCounts,
    loading,
    uploading,
    previewLoading,
    previewError,
    previewUrl,
    previewText,
    fetchAttachments,
    fetchAttachmentCounts,
    getAttachmentCount,
    uploadFile,
    downloadFile,
    loadPreview,
    clearPreview,
    removeAttachment,
    reset,
    resetCounts
  };
});
